import { type Communication, type Unit } from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';

import { type CommunicationCategory } from 'src/hooks/CommunicationsContext';

export type CommunicationBodySource =
  | { kind: 'category'; category: CommunicationCategory }
  | { kind: 'lease'; leaseId: string }
  | { kind: 'unit'; unitId: string }
  | { kind: 'building'; buildingId: string }
  | { kind: 'owner'; ownerId: string; ownerUnits: Unit[] };

export interface CachedCommunicationBody {
  body: string;
  version: number | null;
}

export type CommunicationBodiesById = Record<string, CachedCommunicationBody>;

type CommunicationBodyFields = Pick<Communication, 'id' | 'body' | '_version'>;

export interface OwnerEntityIds {
  ownerLeaseIds: string[];
  ownerUnitIds: string[];
  ownerBuildingIds: string[];
}

export const getCommunicationBodySourceKey = (source: CommunicationBodySource): string => {
  switch (source.kind) {
    case 'category':
      return `category:${source.category}`;
    case 'lease':
      return `lease:${source.leaseId}`;
    case 'unit':
      return `unit:${source.unitId}`;
    case 'building':
      return `building:${source.buildingId}`;
    case 'owner':
    default:
      return `owner:${source.ownerId}`;
  }
};

export const getOwnerEntityIds = (ownerUnits: Unit[]): OwnerEntityIds =>
  ownerUnits.reduce<OwnerEntityIds>(
    (acc, ownerUnit) => {
      if (ownerUnit.id) {
        acc.ownerUnitIds.push(ownerUnit.id);
      }
      if (ownerUnit.buildingId) {
        acc.ownerBuildingIds.push(ownerUnit.buildingId);
      }
      ownerUnit.leases?.forEach((lease) => acc.ownerLeaseIds.push(lease.id));
      return acc;
    },
    { ownerLeaseIds: [], ownerUnitIds: [], ownerBuildingIds: [] }
  );

/** An unknown version is never newer, so a loaded body is not dropped on a guess. */
export const isNewerVersion = (version?: number | null, referenceVersion?: number | null): boolean =>
  !isNil(version) && !isNil(referenceVersion) && version > referenceVersion;

const getCachedBody = (
  communication: Pick<Communication, 'id' | '_version'>,
  bodiesById: CommunicationBodiesById
): string | null => {
  const cached = bodiesById[communication.id];
  if (isNil(cached)) {
    return null;
  }
  return isNewerVersion(communication._version, cached.version) ? null : cached.body;
};

export const mergeCommunicationBodies = (
  current: CommunicationBodiesById,
  communications: CommunicationBodyFields[]
): CommunicationBodiesById =>
  communications.reduce<CommunicationBodiesById>(
    (acc, communication) => {
      if (!isNil(communication.body) && !isNewerVersion(acc[communication.id]?.version, communication._version)) {
        acc[communication.id] = { body: communication.body, version: communication._version ?? null };
      }
      return acc;
    },
    { ...current }
  );

export const withEmptyBodiesForMissing = (
  requestedIds: string[],
  communications: CommunicationBodyFields[]
): CommunicationBodyFields[] => {
  const communicationsById = new Map(communications.map((communication) => [communication.id, communication]));
  return requestedIds.map((id) => communicationsById.get(id) ?? { id, body: '' });
};

export const getKnownCommunicationBody = (
  communication: CommunicationBodyFields,
  bodiesById: CommunicationBodiesById
): string => communication.body ?? getCachedBody(communication, bodiesById) ?? '';

export const withKnownBody = <T extends CommunicationBodyFields>(
  communication: T,
  bodiesById: CommunicationBodiesById
): T => {
  if (!isNil(communication.body)) {
    return communication;
  }
  const cachedBody = getCachedBody(communication, bodiesById);
  return isNil(cachedBody) ? communication : { ...communication, body: cachedBody };
};

const isBodyMissing = (communication: CommunicationBodyFields, bodiesById: CommunicationBodiesById): boolean =>
  isNil(communication.body) && isNil(getCachedBody(communication, bodiesById));

export const getCommunicationIdsWithoutBody = (
  communications: CommunicationBodyFields[],
  bodiesById: CommunicationBodiesById
): string[] =>
  communications.reduce<string[]>((acc, communication) => {
    if (isBodyMissing(communication, bodiesById)) {
      acc.push(communication.id);
    }
    return acc;
  }, []);

/**
 * Changes when a missing body is requested for another row version, or when a load lands a version that is still too
 * old, so the caller requests it again. A repeated stale answer leaves it unchanged, which keeps the retries bounded.
 */
export const getMissingCommunicationBodiesKey = (
  communications: CommunicationBodyFields[],
  bodiesById: CommunicationBodiesById
): string =>
  communications
    .reduce<string[]>((acc, communication) => {
      if (isBodyMissing(communication, bodiesById)) {
        acc.push(`${communication.id}:${communication._version ?? ''}:${bodiesById[communication.id]?.version ?? ''}`);
      }
      return acc;
    }, [])
    .join(',');
